import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import * as loginService from "../../../auth/services/auth.service";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { UserLogin } from "../../../auth/login/models";
import { Link, useNavigate } from "react-router-dom";
import { getLoginMessageError, removeLoginMessageError } from "../../../shared/services/local-storage/login-message-error.service";
import { useTranslation } from "../../../i18n/useTranslation";
import { ACCOUNT_PATHS, FORGOTTEN_PASSWORD_PATHS } from "../../../shared/constants/breadcrumb.constants";
import WelcomeResetBanner from "./WelcomeResetBanner";

interface Props {
    redirectTo: string;
}

const LoginFormInside: React.FC<Props> = ({redirectTo}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation('translation');
    const _redirectTo: string = '/';

    useEffect(() => {
        setErrorMessage(getLoginMessageError());
    }, []);

    const logIn = async (email: string, password: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            loginService.logIn({loginIdentifier: email, password: password} as UserLogin, navigate, redirectTo ?? _redirectTo, () => {
                setErrorMessage(getLoginMessageError());
                reject();
            });
        });
    };
    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: Yup.object({
        email: Yup.string()
            .test(
                "email-or-login",
                t("loginForm.INVALID_EMAIL_OR_LOGIN"),
                (value) => {
                    if (!value) {
                        return false;
                    }
                    if (value.includes('@')) {
                        return Yup.string().email().isValidSync(value);
                    }
                    return value.length >= 3;
                }
            )
        .required(t("loginForm.REQUIRED_EMAIL_OR_LOGIN")),
        password: Yup.string()
            .required(t("loginForm.REQUIRED_PASSWORD")),
        }),
        onSubmit: async (values) => {
            setErrorMessage(null);
            setLoading(true);
        try {
            await logIn(values.email, values.password);
        } catch (err: any) {
            setErrorMessage(getLoginMessageError());
            removeLoginMessageError();
        } finally {
            setLoading(false);
        }
        },
    });

    return (
        <Box
            component="form"
            onSubmit={formik.handleSubmit}
            noValidate
            mb={5}
            sx={{
                width: 500,
                mx: "auto",
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <WelcomeResetBanner
                onResetClick={() => navigate(FORGOTTEN_PASSWORD_PATHS[0].link)}
            />
            <Typography variant="h5" align="center">
                {t("loginForm.SIGN_IN")}
            </Typography>

            {errorMessage && (
                <Typography color="error" align="center">
                    {t(errorMessage)}
                </Typography>
            )}

            <TextField
                InputLabelProps={{shrink: true}}
                fullWidth
                id="email"
                name="email"
                label={t("EMAIL")}
                variant="outlined"
                size="small"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                autoComplete="username"
            />

            <TextField
                InputLabelProps={{shrink: true}}
                fullWidth
                id="password"
                name="password"
                size="small"
                label={t("loginForm.password")}
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                autoComplete="current-password"
                InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <Tooltip title={showPassword ? t('HIDE') : t('SHOW')}>
                                <IconButton
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </Tooltip>
                    </InputAdornment>
                ),
                }}
            />
        
            <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                    height: 42,
                    backgroundColor: "#821818",
                    color: "#fff",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                        backgroundColor: "#821818",
                        boxShadow: "none",
                    },
                    "&:active": {
                        boxShadow: "none",
                    },
                    "&:focus": {
                        outline: "none",
                    },
                }}
                disableRipple
            >
                {loading ? <CircularProgress size={22} color="inherit" /> : t("loginForm.submit")}
            </Button>
            <Box>
                <Typography>
                    <Link to={FORGOTTEN_PASSWORD_PATHS[0].link}>{t('loginForm.forgotPassword')}</Link>
                </Typography>
                <Typography>
                    <Link to={ACCOUNT_PATHS[0].link}>{t('loginForm.registerNow')}</Link>
                </Typography>
            </Box>
        </Box>
    );
};

export default LoginFormInside;